import type { RoomDto } from '@room-mates/shared';
import { type FormEvent, type ReactElement, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useFieldErrors } from '../../shared/hooks/useFieldErrors.js';
import { useTranslation } from '../../shared/i18n/useLanguage.js';
import { useGuests } from '../guests/guestHooks.js';
import {
  Button,
  Card,
  EmptyState,
  ErrorAlert,
  Field,
  LoadingBlock,
  PageHeader,
  TextInput,
} from '../../shared/ui/index.js';
import { useHouse, useUpdateHouse } from './houseHooks.js';
import { RoomCard } from './RoomCard.js';
import { RoomDialog } from './RoomDialog.js';

export function HouseDetailPage(): ReactElement {
  const t = useTranslation();
  const { houseId = '' } = useParams();
  const house = useHouse(houseId);
  const guests = useGuests();
  const [addingRoom, setAddingRoom] = useState(false);
  const [editingRoom, setEditingRoom] = useState<RoomDto | null>(null);

  if (house.isPending) {
    return <LoadingBlock />;
  }

  if (house.error !== null || house.data === undefined) {
    return (
      <div className="stack">
        <ErrorAlert error={house.error} />
        <Link to="/houses">{t.houseDetail.backToHouses}</Link>
      </div>
    );
  }

  return (
    <div className="stack stack--loose">
      <PageHeader
        title={house.data.name}
        subtitle={
          <>
            {house.data.address ?? t.common.noAddressRecorded} ·{' '}
            {t.count.rooms(house.data.roomCount)} · {t.count.beds(house.data.bedCount)}
          </>
        }
        actions={<Button onClick={() => setAddingRoom(true)}>{t.houseDetail.addRoom}</Button>}
      />

      <HouseSettings
        // Remounting on a server-side change resets the draft to the saved
        // values, which is exactly what should happen after a successful save
        // and never happens while the user is mid-edit.
        key={`${house.data.name}|${house.data.address ?? ''}`}
        houseId={houseId}
        name={house.data.name}
        address={house.data.address}
      />

      {house.data.rooms.length === 0 ? (
        <EmptyState
          title={t.houseDetail.noRoomsTitle}
          description={t.houseDetail.noRoomsDescription}
          action={<Button onClick={() => setAddingRoom(true)}>{t.houseDetail.addRoom}</Button>}
        />
      ) : (
        <div className="stack">
          {house.data.rooms.map((room) => (
            <RoomCard
              key={room.id}
              houseId={houseId}
              room={room}
              guests={guests.data ?? []}
              onEdit={() => setEditingRoom(room)}
            />
          ))}
        </div>
      )}

      {addingRoom ? <RoomDialog houseId={houseId} onClose={() => setAddingRoom(false)} /> : null}
      {editingRoom !== null ? (
        <RoomDialog houseId={houseId} room={editingRoom} onClose={() => setEditingRoom(null)} />
      ) : null}
    </div>
  );
}

function HouseSettings({
  houseId,
  name,
  address,
}: {
  houseId: string;
  name: string;
  address: string | null;
}): ReactElement {
  const t = useTranslation();
  const updateHouse = useUpdateHouse(houseId);
  const [draftName, setDraftName] = useState(name);
  const [draftAddress, setDraftAddress] = useState(address ?? '');
  const fieldError = useFieldErrors(updateHouse.error);

  const dirty = draftName !== name || draftAddress !== (address ?? '');

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    updateHouse.mutate({
      name: draftName,
      address: draftAddress.trim() === '' ? null : draftAddress,
    });
  };

  return (
    <Card>
      <form className="stack" onSubmit={submit} noValidate>
        <h2>{t.houseDetail.detailsTitle}</h2>
        <ErrorAlert error={updateHouse.error} />
        <div className="row" style={{ alignItems: 'flex-start' }}>
          <div style={{ flex: '1 1 200px' }}>
            <Field label={t.houseDetail.nameLabel} error={fieldError('name')}>
              {(props) => (
                <TextInput
                  {...props}
                  value={draftName}
                  onChange={(event) => setDraftName(event.target.value)}
                />
              )}
            </Field>
          </div>
          <div style={{ flex: '2 1 320px' }}>
            <Field label={t.houseDetail.addressLabel} error={fieldError('address')}>
              {(props) => (
                <TextInput
                  {...props}
                  value={draftAddress}
                  onChange={(event) => setDraftAddress(event.target.value)}
                />
              )}
            </Field>
          </div>
        </div>
        <div className="row row--end">
          <Button type="submit" disabled={!dirty} loading={updateHouse.isPending}>
            {t.common.saveChanges}
          </Button>
        </div>
      </form>
    </Card>
  );
}
